import clsx from 'clsx';
import React, { Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import SignupForm from '@components/pages/Subscribe/Forms/SignupForm';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import { READ_ONLY_MODE, READ_ONLY_PHRASE } from '@src/env';
import { SelectedOffer } from '@utils/backend-api/subscribe';
import { FormLocation, LeadTypes } from '@utils/enums';

const PaymentForm = lazy(() => import('@components/pages/Subscribe/Forms/PaymentForm'));

const queryClient = new QueryClient();

export interface SubscriptionFormProps {
  buttonText: string;
  className?: string;
  isOfferSelected: boolean;
  leadType?: LeadTypes;
  presentedOfferCodes: string[];
  selectedOffer: SelectedOffer;
  stripeCustomerId?: string;
}

const SubscribeForm = (props: SubscriptionFormProps) => {
  const {
    user: { stripeCustomerId },
  } = useSubscribeForm();
  const showPaymentForm = stripeCustomerId && props.isOfferSelected;
  const { isPro } = props.selectedOffer;
  return (
    <div>
      {READ_ONLY_MODE ? (
        <div className="warning mt-28">
          <h3>Purchases are temporarily unavailable</h3>
          Sorry, we&apos;re doing some necessary site maintenance now, and this feature isn&apos;t
          available. Please try again in {READ_ONLY_PHRASE || '10-15 minutes'}. Thanks for your
          patience!
        </div>
      ) : (
        <QueryClientProvider client={queryClient}>
          <SignupForm
            presentedOfferCodes={props.presentedOfferCodes}
            formLocation={FormLocation.Subscribe}
            leadType={props.leadType ?? LeadTypes.SubscribePage}
            selectedOffer={props.selectedOffer}
            isOfferSelected={props.isOfferSelected}
            isDualSubscribePage
          />
          {stripeCustomerId && (
            <div className="m-auto max-w-md sm:max-w-full">
              <SectionBreakTitle
                title="2. Payment information"
                titleClassName="text-black"
                color={props.selectedOffer.isPro ? SectionColor.Gold : SectionColor.Magenta}
                className={clsx('mb-24 mt-32 border-0 border-t border-solid', {
                  'border-pro-gold': isPro,
                  'border-primary-magenta': !isPro,
                })}
                isDualSubscribeExperiment
              />
              {showPaymentForm && (
                <Suspense>
                  <PaymentForm {...props} stripeCustomerId={stripeCustomerId} isDualSubscribePage />
                </Suspense>
              )}
            </div>
          )}
        </QueryClientProvider>
      )}
    </div>
  );
};

export default SubscribeForm;
